import {
    DescriptionEntry,
    SelectEntry,
    isSelectEntryEdited,
    TextAreaEntry,
    isTextAreaEntryEdited,
    TextFieldEntry,
    isTextFieldEntryEdited,
    ListGroup,
} from "@bpmn-io/properties-panel";
import { useService } from "bpmn-js-properties-panel";
import { is } from "bpmn-js/lib/util/ModelUtil";

// Import your custom list group entries.
import scriptRequestProps from "../request/RequestProperties";
import scriptResponseProps from "../response/ResponseProperties";

import { isReferenceVald, nextId } from "../utils";

const LOW_PRIORITY = 500;

/**
 * A provider with a `#getGroups(element)` method
 * that exposes groups for a diagram element.
 *
 * @param {PropertiesPanel} propertiesPanel
 * @param {Function} translate
 */
export default function ScriptPropertiesProvider(
    propertiesPanel,
    injector,
    translate,
    moddle
) {
    /**
     * Return the groups provided for the given element.
     *
     * @param {DiagramElement} element
     *
     * @return {(Object[]) => (Object[])} groups middleware
     */
    this.getGroups = function (element) {
        /**
         * We return a middleware that modifies
         * the existing groups.
         *
         * @param {Object[]} groups
         *
         * @return {Object[]} modified groups
         */
        return function (groups) {
            if (is(element, "bpmn:ScriptTask")) {
                for (let group in extensions) {
                    if (extensions[group].scripts)
                        groups.push(
                            createGroupExtension(
                                group,
                                extensions[group].name,
                                element,
                                injector,
                                translate
                            )
                        );
                }

                groups.push(createGroupScript(element, injector, translate));
                // groups.push(createGroupPublish(element, injector, translate));
            }

            return groups;
        };
    };

    // Register our custom properties provider.
    // Use a lower priority to ensure it is loaded after
    // the basic BPMN properties.
    propertiesPanel.registerProvider(LOW_PRIORITY, this);
}

function createGroupExtension(id, name, element, injector, translate) {
    return {
        id: id,
        label: translate(name),
        entries: [
            {
                id: id,
                component: Extension,
                isEdited: isSelectEntryEdited,
                element,
            },
        ],
    };
}

function createGroupScript(element, injector, translate) {
    let entries = [
        {
            id: "scriptType",
            component: ScriptType,
            isEdited: isSelectEntryEdited,
            element,
        },
        {
            id: "scriptLanguage",
            component: ScriptLanguage,
            isEdited: isSelectEntryEdited,
            element,
        },
        {
            id: "scriptReference",
            component: ScriptReference,
            isEdited: isTextFieldEntryEdited,
        },
        {
            id: "scriptName",
            component: ScriptName,
            isEdited: isTextFieldEntryEdited,
        },
        {
            id: "request",
            label: translate("Request"),
            component: ListGroup,
            ...scriptRequestProps({ element, injector }),
        },
        {
            id: "response",
            label: translate("Response"),
            component: ListGroup,
            ...scriptResponseProps({ element, injector }),
        },
        {
            id: "description-footer",
            forId: "script-group",
            value: (
                <div>
                    <label class="bio-properties-panel-description">
                        *Required fields
                    </label>
                    <p>
                        Request and response represent values for the process
                        store.
                    </p>
                    <p>
                        Publish a process script file to the
                        Brixx-Process-Engine.
                    </p>
                </div>
            ),
            component: ScriptDescription,
        },
        {
            component: PublishScript,
        },
    ];

    switch (element.businessObject.scriptType) {
        case "REFERENCE":
            delete element.businessObject["scriptSource"];
            break;
        case "SOURCE":
            delete element.businessObject["scriptReference"];
            entries = [
                {
                    id: "scriptType",
                    component: ScriptType,
                    isEdited: isSelectEntryEdited,
                    element,
                },
                {
                    id: "scriptLanguage",
                    component: ScriptLanguage,
                    isEdited: isSelectEntryEdited,
                    element,
                },
                {
                    id: "scriptName",
                    component: ScriptName,
                    isEdited: isTextFieldEntryEdited,
                },
                {
                    id: "scriptSource",
                    component: ScriptSource,
                    isEdited: isTextAreaEntryEdited,
                },
                {
                    id: "request",
                    label: translate("Request"),
                    component: ListGroup,
                    ...scriptRequestProps({ element, injector }),
                },
                {
                    id: "response",
                    label: translate("Response"),
                    component: ListGroup,
                    ...scriptResponseProps({ element, injector }),
                },
                {
                    id: "description-footer",
                    forId: "script-group",
                    value: (
                        <div>
                            <label class="bio-properties-panel-description">
                                *Required fields
                            </label>
                            <p>
                                Request and response represent values for the
                                process store.
                            </p>
                        </div>
                    ),
                    component: ScriptDescription,
                },
            ];
            break;
        default:
            delete element.businessObject["scriptType"];
            delete element.businessObject["scriptLanguage"];
    }

    return {
        id: "script-group",
        label: translate("Script"),
        entries,
    };
}

function createGroupPublish(element, injector, translate) {
    return {
        id: "publish-group",
        label: translate("Publish"),
        entries: [
            {
                id: "description-footer",
                forId: "publish-group",
                value: (
                    <div>
                        <p>
                            Publish a process script file to the
                            Brixx-Process-Engine.
                        </p>
                    </div>
                ),
                component: PublishDescription,
            },
            {
                component: PublishScript,
            },
        ],
    };
}

function ScriptType(props) {
    const { id, element } = props;
    const modeling = useService("modeling");
    const translate = useService("translate");
    const debounce = useService("debounceInput");
    const types = [];

    const getValue = () => {
        return element.businessObject.scriptType || undefined;
    };

    const setValue = (value) => {
        return modeling.updateProperties(element, {
            scriptType: value,
            scriptName: "",
        });
    };

    const getOptions = () => {
        return [
            { label: translate("<none>"), value: undefined },
            { label: "Reference", value: "REFERENCE" },
            { label: "Source", value: "SOURCE" },
            ...types.map((type) => ({
                label: type,
                value: type,
            })),
        ];
    };

    const entry = (
        <SelectEntry
            id={id}
            element={element}
            label={translate("Type")}
            description={translate("An optional script type.")}
            getValue={getValue}
            setValue={setValue}
            getOptions={getOptions}
            debounce={debounce}
        />
    );

    return entry;
}

function ScriptLanguage(props) {
    const { id, element } = props;
    const modeling = useService("modeling");
    const translate = useService("translate");
    const debounce = useService("debounceInput");
    const types = [];

    const getValue = () => {
        return element.businessObject.scriptLanguage || undefined;
    };

    const setValue = (value) => {
        let scriptReference = undefined;
        let scriptName = "";

        return modeling.updateProperties(element, {
            scriptLanguage: value,
            scriptReference: scriptReference,
            scriptName: scriptName,
        });
    };

    const getOptions = () => {
        return [
            { label: translate("<none>"), value: undefined },
            { label: "JavaScript", value: "JavaScript" },
            { label: "Python", value: "Python" },
            ...types.map((type) => ({
                label: type,
                value: type,
            })),
        ];
    };

    const entry = (
        <SelectEntry
            id={id}
            element={element}
            label={translate("Lanuguage")}
            description={translate("An optional script language.")}
            getValue={getValue}
            setValue={setValue}
            getOptions={getOptions}
            debounce={debounce}
        />
    );

    return entry;
}

function validateReference(idValue, translate) {
    if (containsSpace(idValue)) {
        return translate("ID must not contain spaces.");
    }

    if (!ID_REGEX.test(idValue)) {
        if (QNAME_REGEX.test(idValue)) {
            return translate("ID must not contain prefix.");
        }

        return translate("ID must be a valid QName.");
    }
}

function isReferenceValid(element, idValue, translate) {
    const assigned = element.$model.ids.assigned(idValue);
    const idAlreadyExists = assigned && assigned !== element;

    if (!idValue) {
        return translate("ID must not be empty.");
    }

    if (idAlreadyExists) {
        return translate("ID must be unique.");
    }

    return validateReference(idValue, translate);
}

function Extension(props) {
    const { id, element } = props;
    const modeling = useService("modeling");
    const translate = useService("translate");
    const debounce = useService("debounceInput");
    const types = [];

    const getValue = () => {
        let extension = undefined;

        if (extensions[id].scripts)
            for (let item of extensions[id].scripts) {
                if (item.id === element.businessObject.scriptReference) {
                    extension = true;
                }
            }

        if (extension) return element.businessObject.scriptReference;

        return extension;
    };

    const setValue = (value) => {
        let extension = undefined;
        let scriptLanguage = element.businessObject.scriptLanguage || undefined;
        let scriptName = "";

        if (value === "") value = undefined;
        if (value) extension = value;

        if (extensions[id].scripts)
            for (let item of extensions[id].scripts) {
                if (item.id === value) {
                    scriptName = item.name;
                    scriptLanguage = item.language;
                }
            }

        return modeling.updateProperties(element, {
            extension: extension,
            scriptType: "REFERENCE",
            scriptLanguage: scriptLanguage,
            scriptReference: value,
            scriptName: scriptName,
        });
    };

    const getOptions = () => {
        const options = [];
        if (extensions[id].scripts)
            extensions[id].scripts.map((item) => {
                options.push({ label: translate(item.name), value: item.id });
            });

        return [
            { label: translate("<none>"), value: undefined },
            ...options.map((item) => item),
            ...types.map((type) => ({
                label: type,
                value: type,
            })),
        ];
    };

    const entry = (
        <SelectEntry
            id={id}
            element={element}
            label={translate("Extension")}
            getValue={getValue}
            setValue={setValue}
            getOptions={getOptions}
            debounce={debounce}
        />
    );

    return entry;
}

function ScriptReference(props) {
    const { element, id } = props;
    const modeling = useService("modeling");
    const translate = useService("translate");
    const debounce = useService("debounceInput");

    const getValue = () => {
        if (element.businessObject.scriptReference === undefined)
            setValue(nextId("Reference_"));

        return element.businessObject.scriptReference;
    };

    const setValue = (value) => {
        let extension = undefined;
        let scriptLanguage = element.businessObject.scriptLanguage || undefined;
        let scriptName = "";

        for (let group in extensions) {
            if (extensions[group].scripts)
                for (let item of extensions[group].scripts) {
                    if (item.id === value) {
                        extension = true;
                        scriptLanguage = item.language;
                        scriptName = item.name;
                    }
                }
        }

        return modeling.updateProperties(element, {
            extension: extension,
            scriptLanguage: scriptLanguage,
            scriptReference: value,
            scriptName: scriptName,
        });
    };

    const validate = (value) => {
        return isReferenceVald(value, translate);
    };

    const entry = (
        <TextFieldEntry
            id={id}
            element={element}
            label={translate("*Reference")}
            getValue={getValue}
            setValue={setValue}
            debounce={debounce}
            validate={validate}
        />
    );

    return entry;
}

function ScriptSource(props) {
    const { element, id } = props;
    const modeling = useService("modeling");
    const translate = useService("translate");
    const debounce = useService("debounceInput");

    const getValue = () => {
        return element.businessObject.scriptSource || "";
    };

    const setValue = (value) => {
        return modeling.updateProperties(element, {
            scriptSource: value,
        });
    };

    const label = `${
        element.businessObject.scriptLanguage || "JavaScript"
    } source code`;

    const entry = (
        <div class="bio-properties-panel-group">
            <TextAreaEntry
                id={id}
                element={element}
                label={translate(label)}
                rows={5}
                getValue={getValue}
                setValue={setValue}
                debounce={debounce}
            />
        </div>
    );

    return entry;
}

function ScriptName(props) {
    const { element, id } = props;
    const modeling = useService("modeling");
    const translate = useService("translate");
    const debounce = useService("debounceInput");

    const getValue = () => {
        return element.businessObject.scriptName || "";
    };

    const setValue = (value) => {
        return modeling.updateProperties(element, {
            scriptName: value,
        });
    };

    const entry = (
        <div class="bio-properties-panel-group">
            <TextFieldEntry
                id={id}
                element={element}
                label={translate("Name")}
                description={translate("An optional script name.")}
                getValue={getValue}
                setValue={setValue}
                debounce={debounce}
            />
        </div>
    );

    return entry;
}

function ScriptDescription(props) {
    const { element, forId, value } = props;
    const entry = (
        <div style="margin-left:12px; margin-right:30px">
            <DescriptionEntry element={element} forId={forId} value={value} />
        </div>
    );

    return entry;
}

function PublishScript(props) {
    const { element } = props;
    const className = publishEnabled ? "icon-publish" : "icon-publish-disabled";
    let fileSuffix = "js";

    switch (element.businessObject.scriptLanguage) {
        case "Python":
            fileSuffix = "py";
            break;
    }
    let fileName = `${element.businessObject.scriptReference}.${fileSuffix}`;
    const entry = (
        <div>
            <form action="">
                <input
                    type="file"
                    name="file"
                    id="file-input"
                    class="file-input"
                />
            </form>
            <button
                class="button-publish-properties"
                onClick={() => publishProcessScript(fileName)}
                title="Publish script to Brixx-Process-Engine"
            >
                <span class={className}></span>
            </button>
        </div>
    );

    return entry;
}

function PublishDescription(props) {
    const { element, forId, value } = props;
    const entry = (
        <div style="margin-left:12px; margin-right:30px">
            <DescriptionEntry element={element} forId={forId} value={value} />
        </div>
    );

    return entry;
}

ScriptPropertiesProvider.$inject = [
    "propertiesPanel",
    "injector",
    "translate",
    "moddle",
];
